import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { Flex } from '../../components/Flex'
import { Price } from '../../components/Price'
import { PriceLabel } from '../../components/PriceLabel'
import { Text, Title } from '../../components/Text'
import { PriceVisibility } from '../../containers/PriceVisibility'
import { useDateFormat } from '../../hooks/useDateFormat'
import { getPricesMode, getPrimaryIdentifier, PrimaryIdentifier } from '../../redux/config'
import { DomainType } from '../../redux/domains'
import { OrderItemStatusCode } from '../../redux/order'
import { getContractType } from '../../redux/user/selectors'
import { createTFunction, getDomainDescription, getTextFromHtml } from '../../utils/helpers'

import { CartItemProps, DescriptionFieldProps, ErrorFieldPros, StyledFieldProps } from './types'
import { DocumentIcon, ProductTitle } from './styled'

const CartField = ({ label, value }: StyledFieldProps) => (
  <Flex pt="10px" flexDirection="row">
    <Title level="small" bold padding="0px 12px 0 0">
      {label}
      {!_.isEmpty(value) ? ':' : ''}
    </Title>
    {!_.isEmpty(value) && <span>{value}</span>}
  </Flex>
)

const ErrorField = ({ label, value, errorMessage }: ErrorFieldPros) => (
  <>
    <Flex pt="10px" flexDirection="row">
      <Title level="small" bold padding="0px 12px 0 0">
        {label}
        {!_.isEmpty(value) ? ':' : ''}
      </Title>
      {!_.isEmpty(value) && (
        <span>
          <Text m={1} color="danger">
            {value}
          </Text>
        </span>
      )}
    </Flex>
    {!_.isEmpty(errorMessage) && (
      <Flex pt="10px" flexDirection="row">
        <Text m={1} color="danger">
          {errorMessage}
        </Text>
      </Flex>
    )}
  </>
)

const DescriptionField = ({ label, value }: DescriptionFieldProps) => (
  <Flex pt="10px" flexDirection="row" flexWrap="wrap">
    <span>
      <ProductTitle level="small" bold padding="0px 12px 0 0">
        {label}
        {!_.isEmpty(value) ? ':' : ''}
      </ProductTitle>
      {value && !_.isEmpty(value) && <span>{getTextFromHtml(value)}</span>}
    </span>
  </Flex>
)

export const SidebarCartItem = ({
  data,
  domainsData,
  umbracoData,
  displayProductDescription = false
}: CartItemProps) => {
  const { t } = useTranslation(['form', 'core', 'checkout'])
  const tf = createTFunction('form:', t)
  const tc = createTFunction('core:', t)

  const { formatDate } = useDateFormat()

  const contractType = useSelector(getContractType)
  const pricesMode = useSelector(getPricesMode)
  const primaryIdentifier = useSelector(getPrimaryIdentifier)
  const deliveryOption = getDomainDescription(DomainType.DeliveryOption, data.deliveryOptionCode, domainsData)

  return (
    <Flex width={1} pt="24px" pb="8px" mb="8px" flexWrap="wrap" flexDirection="column">
      {primaryIdentifier === PrimaryIdentifier.Report && (
        <>
          <Flex>
            <DocumentIcon size="large" />
            <Text color="primary" fontSize="24px" pl="12px" bold>
              {umbracoData?.name}
            </Text>
          </Flex>

          <CartField label={tf('Company')} value={data.companyName} />
        </>
      )}

      {primaryIdentifier === PrimaryIdentifier.Company && (
        <>
          <Flex>
            <DocumentIcon size="small" />
            <Text color="primary" fontSize="16px" pl="12px" bold>
              {data.companyName}
            </Text>
          </Flex>
          <CartField label={tf('ReportName')} value={umbracoData?.name} />
        </>
      )}

      {data.documentDate ? (
        <CartField
          label={tf('DocumentDate')}
          value={formatDate(new Date(data.documentDate), { dateStyle: 'medium' })}
        />
      ) : (
        <>
          <CartField
            label={tf('Language')}
            value={getDomainDescription(DomainType.Culture, data.languageCode, domainsData)}
          />
          {deliveryOption && (
            <CartField
              label={tf('DeliveryOption')}
              value={getDomainDescription(DomainType.DeliveryOption, data.deliveryOptionCode, domainsData)}
            />
          )}
          {data.lastUpdateDate && (
            <CartField
              label={tf('LastUpdate')}
              value={formatDate(new Date(data.lastUpdateDate), { dateStyle: 'medium' })}
            />
          )}
        </>
      )}
      <PriceVisibility>
        {data.statusCode !== OrderItemStatusCode.Error && (
          <CartField
            label={PriceLabel({ pricesMode, label: tc('Price'), netLabel: tc('PriceSSTExcluded') })}
            value={Price({
              pricesMode,
              contractType,
              grossValue: data.valueWithTaxFormatted,
              netValue: data.netValueFormatted
            })}
          />
        )}
        {data.statusCode === OrderItemStatusCode.Error && (
          <ErrorField
            label={PriceLabel({ pricesMode, label: tc('Price'), netLabel: tc('PriceSSTExcluded') })}
            value={t('checkout:ItemErrorTitle')}
            errorMessage={t('checkout:ItemError')}
          />
        )}
      </PriceVisibility>
      {displayProductDescription && (
        <DescriptionField label={t('checkout:ProductDescription')} value={umbracoData?.previewDescription} />
      )}
    </Flex>
  )
}
